import {observable} from 'mobx';

import {PartialGameDomainModel} from "./partial-game-domain-model";
import {PagingModel} from "../../../infrastructure/models/paging/paging.model";
import {PartialGameResponseApiModel} from "./partial-game-response-api-model";
import {BaseDomainDataSource} from "../../../infrastructure/models/base-domain-data-source";
import {CompetitionGamesDomainModel} from "../../common/competition-games.domain-model";
import {CompetitionDomainModel} from "../../common/competition/models/competition-domain-model";
import {BookmakerDomainModel} from "../../common/bookmaker/models/bookmaker-domain-model";
import {CountryDomainModel} from "../../common/country/models/country-domain-model";
import {SummaryDomainModel} from "../../full-game/models/summary/summary.domain-model";
import {CompetitorDomainModel} from "../../common/competitor/models/competitor-domain-model";
import type {RoundFilterDomainModel} from "./round-filter/round-filter.domain-model";

export class PartialGameDataSource extends BaseDomainDataSource {
  apiModel: PartialGameResponseApiModel;
  @observable games: Array<PartialGameDomainModel>;
  countries: Array<CountryDomainModel>;
  competitions: Array<CompetitionDomainModel>;
  competitors: Array<CompetitorDomainModel>;
  bookmakers: Array<BookmakerDomainModel>;
  @observable paging: PagingModel;
  summary: SummaryDomainModel;
  @observable suggestedGames: Array<PartialGameDomainModel>;
  roundFilters: Array<RoundFilterDomainModel>
  competitionFilters: Array<CompetitionDomainModel>

  constructor(
    apiModel: PartialGameResponseApiModel,
    games: Array<PartialGameDomainModel>,
    countries: Array<CountryDomainModel>,
    competitions: Array<CompetitionDomainModel>,
    competitors: Array<CompetitorDomainModel>,
    bookmakers: Array<BookmakerDomainModel>,
    paging: PagingModel,
    summary: SummaryDomainModel,
    suggestedGames: Array<PartialGameDomainModel>,
    roundFilters: Array<RoundFilterDomainModel>,
    competitionFilters: Array<CompetitionDomainModel>
  ) {
    super(apiModel, apiModel.request);

    this.games = games || [];
    this.countries = countries || [];
    this.competitions = competitions || [];
    this.competitors = competitors || [];
    this.bookmakers = bookmakers || [];
    this.paging = paging || {};
    this.summary = summary;
    this.suggestedGames = suggestedGames || [];
    this.roundFilters = roundFilters || [];
    this.competitionFilters = competitionFilters || [];
  }

  groupGamesByCompetition(liveFilter: Boolean = false): Array<CompetitionGamesDomainModel> {
    const unsortedCompetitions: Array<CompetitionGamesDomainModel> = [];

    this.games.forEach((game: PartialGameDomainModel) => {
      const existingCompetition: CompetitionGamesDomainModel = unsortedCompetitions.find((competitionGames: CompetitionGamesDomainModel) => {
        return competitionGames.id === game.competition.id;
      });

      if (!liveFilter || game.isLive) {
        if (existingCompetition) {
          if (game.isValid) {
            existingCompetition.entities.push(game);
          }
        } else {
          const newCompetitionGames: CompetitionGamesDomainModel = new CompetitionGamesDomainModel(game.competition, [game]);

          unsortedCompetitions.push(newCompetitionGames);
        }
      }
    });

    const sortedCompetitions: Array<CompetitionGamesDomainModel> = unsortedCompetitions.sort((x: CompetitionGamesDomainModel, y: CompetitionGamesDomainModel) => x.groupItem.sortIndex - y.groupItem.sortIndex);

    return sortedCompetitions;
  }

  update(model: PartialGameDataSource = {}) {
    super.update(model);

    const newItems = (model.games || []).filter((newGame: PartialGameDomainModel) => {
      const existingGame = (this.games || []).find((game: PartialGameDomainModel) => game.id === newGame.id);

      if (existingGame && existingGame.updateTime !== newGame.updateTime) {
        Object.assign(existingGame, newGame)
      }

      return !existingGame;
    });

    this.games.push(...newItems);

    const newSuggestedItems = (model.suggestedGames || []).filter((newGame: PartialGameDomainModel) => {
      const existingSuggestedGame = (this.suggestedGames || []).find((game: PartialGameDomainModel) => game.id === newGame.id);

      if (existingSuggestedGame && existingSuggestedGame.updateTime !== newGame.updateTime) {
        Object.assign(existingSuggestedGame, newGame)
      }

      return !existingSuggestedGame;
    });

    this.suggestedGames.push(...newSuggestedItems);

    model.competitions?.forEach((competition) => {
      const existCompetition = this.competitions.find((currentCompetition) => currentCompetition.id === competition.id);

      if(existCompetition) {
        Object.assign(existCompetition, competition);
      } else {
        this.competitions.push(competition);
      }
    })

    model.bookmakers?.forEach((bookmaker) => {
      const existBookmaker = this.bookmakers.find((currentBookmaker) => currentBookmaker.id === bookmaker.id);

      if(existBookmaker) {
        Object.assign(existBookmaker, bookmaker);
      } else {
        this.bookmakers.push(bookmaker);
      }
    })
  }

  get groupedGamesByCompetition(): Array<CompetitionGamesDomainModel> {
    return this.groupGamesByCompetition();
  }

  get groupedGamesByCompetitionLive(): Array<CompetitionGamesDomainModel> {
    return this.groupGamesByCompetition(true);
  }

  get liveGamesCount(): Number {
    const liveGamesCount = this.apiModel.liveGamesCount || 0;

    return liveGamesCount;
  }
}
